import { useState } from "react";
import { CheckCircle2, XCircle, Building2, ArrowUpDown, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";

export default function BankComparison({ banks, requestedAmount }) {
  const [sortBy, setSortBy] = useState("amount"); // amount, payment, rate, term
  const [showAll, setShowAll] = useState(false);

  if (!banks || banks.length === 0) return null;

  const sortedBanks = [...banks].sort((a, b) => {
    switch (sortBy) {
      case "amount":
        return b.max_approved_amount - a.max_approved_amount;
      case "payment":
        return a.estimated_monthly_payment - b.estimated_monthly_payment;
      case "rate":
        return a.interest_rate - b.interest_rate;
      case "term":
        return b.max_term_years - a.max_term_years;
      default:
        return 0;
    }
  });

  const displayedBanks = showAll ? sortedBanks : sortedBanks.slice(0, 3);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Find best in each category
  const bestAmount = Math.max(...banks.map(b => b.max_approved_amount));
  const bestPayment = Math.min(...banks.map(b => b.estimated_monthly_payment));
  const bestRate = Math.min(...banks.map(b => b.interest_rate));
  const bestTerm = Math.max(...banks.map(b => b.max_term_years));

  return (
    <div className="mt-8" data-testid="bank-comparison">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#0F3D3E] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#5BB5A2]" />
            Compare All Banks
          </h2>
          <p className="text-sm text-gray-500">Side-by-side comparison of your matched banks</p>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#5BB5A2]/30 focus:border-[#5BB5A2]"
            data-testid="comparison-sort"
          >
            <option value="amount">Highest Amount</option>
            <option value="payment">Lowest Payment</option>
            <option value="rate">Lowest Rate</option>
            <option value="term">Longest Term</option>
          </select>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-4 font-semibold text-[#0F3D3E] border-b">Bank</th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">
                <button 
                  onClick={() => setSortBy("amount")}
                  className={`flex items-center justify-center gap-1 mx-auto ${sortBy === "amount" ? "text-[#5BB5A2]" : ""}`}
                >
                  Loan Amount
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">
                <button 
                  onClick={() => setSortBy("payment")}
                  className={`flex items-center justify-center gap-1 mx-auto ${sortBy === "payment" ? "text-[#5BB5A2]" : ""}`}
                >
                  Monthly Payment
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">
                <button 
                  onClick={() => setSortBy("rate")}
                  className={`flex items-center justify-center gap-1 mx-auto ${sortBy === "rate" ? "text-[#5BB5A2]" : ""}`}
                >
                  Interest Rate
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">
                <button 
                  onClick={() => setSortBy("term")}
                  className={`flex items-center justify-center gap-1 mx-auto ${sortBy === "term" ? "text-[#5BB5A2]" : ""}`}
                >
                  Term
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">TDSR</th>
              <th className="text-center p-4 font-semibold text-[#0F3D3E] border-b">Total Interest</th>
            </tr>
          </thead>
          <tbody>
            {displayedBanks.map((bank, index) => {
              const totalPaid = bank.estimated_monthly_payment * bank.max_term_years * 12;
              const totalInterest = totalPaid - bank.max_approved_amount;
              const isBestAmount = bank.max_approved_amount === bestAmount;
              const isBestPayment = bank.estimated_monthly_payment === bestPayment;
              const isBestRate = bank.interest_rate === bestRate;
              const isBestTerm = bank.max_term_years === bestTerm;

              return (
                <tr 
                  key={index} 
                  className={`border-b hover:bg-gray-50/50 ${index === 0 ? "bg-[#5BB5A2]/5" : ""}`}
                  data-testid={`comparison-row-${index}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {bank.bank_logo ? (
                        <img src={bank.bank_logo} alt={bank.bank_name} className="h-8 w-auto object-contain" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#0F3D3E]">{bank.bank_name}</p>
                        {index === 0 && (
                          <span className="text-xs bg-[#E8B84A] text-white px-2 py-0.5 rounded-full">Best Match</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`font-semibold ${isBestAmount ? "text-[#5BB5A2]" : "text-[#0F3D3E]"}`}>
                        {formatCurrency(bank.max_approved_amount)}
                      </span>
                      {isBestAmount && <CheckCircle2 className="w-4 h-4 text-[#5BB5A2]" />}
                    </div>
                    {bank.max_approved_amount < requestedAmount && (
                      <span className="text-xs text-amber-600">Adjusted</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`font-semibold ${isBestPayment ? "text-[#5BB5A2]" : "text-[#0F3D3E]"}`}>
                        {formatCurrency(bank.estimated_monthly_payment)}/mo
                      </span>
                      {isBestPayment && <CheckCircle2 className="w-4 h-4 text-[#5BB5A2]" />}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`font-semibold ${isBestRate ? "text-[#5BB5A2]" : "text-[#0F3D3E]"}`}>
                        {bank.interest_rate}%
                      </span>
                      {isBestRate && <CheckCircle2 className="w-4 h-4 text-[#5BB5A2]" />}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`font-semibold ${isBestTerm ? "text-[#5BB5A2]" : "text-[#0F3D3E]"}`}>
                        {bank.max_term_years} years
                      </span>
                      {isBestTerm && <CheckCircle2 className="w-4 h-4 text-[#5BB5A2]" />}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className={`font-medium ${bank.tdsr_percentage > 40 ? "text-amber-600" : "text-[#0F3D3E]"}`}>
                      {bank.tdsr_percentage?.toFixed(1)}%
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-gray-600">{formatCurrency(totalInterest)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {banks.length > 3 && (
        <div className="text-center mt-4">
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
            data-testid="show-all-banks"
          >
            {showAll ? "Show Less" : `Show All ${banks.length} Banks`}
          </Button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4 text-[#5BB5A2]" />
          <span>Best in category</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="bg-[#E8B84A] text-white px-2 py-0.5 rounded-full text-xs">Best Match</span>
          <span>Recommended option</span>
        </div>
      </div>
    </div>
  );
}
